import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageSeo from "../components/PageSeo";
import { useApp } from "../context/AppContext";
import {
  MECHANIC_BRANDS,
  MECHANIC_SERVICES,
  getMechanicWithReviews,
  getMechanicsByCity,
  submitMechanic,
  submitReview,
  toWhatsAppLink,
} from "../lib/mechanics";
import { INDIAN_STATES, getDistrictsForState } from "../data/indianStates";
import "./MechanicFinder.css";

const BRAND_OPTIONS = ["all", ...MECHANIC_BRANDS.filter((brand) => brand !== "all")];
const SERVICE_OPTIONS = ["all", ...MECHANIC_SERVICES];

const HERO_STATS = [
  { value: "Verified only", label: "Login-protected listings" },
  { value: "WhatsApp first", label: "Chat directly with shops" },
  { value: "Rider reviews", label: "Real community feedback" },
];

function titleCase(value = "") {
  return String(value)
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatTime(value) {
  if (!value) return "Open hours not shared";
  const time = String(value).slice(0, 5);
  return convertTo24HourFormat(time);
}

function convertTo24HourFormat(time24) {
  if (!time24) return "";
  const [hours, minutes] = time24.split(":");
  const h = parseInt(hours, 10);
  const m = parseInt(minutes, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
}

function formatPhoneForDisplay(phone) {
  if (!phone) return "";
  const cleaned = String(phone).replace(/\D/g, "");
  if (cleaned.startsWith("91")) {
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8)}`;
  }
  return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
}

function starString(rating = 0) {
  const fullStars = Math.round(Number(rating) || 0);
  return "★★★★★".slice(0, fullStars).padEnd(5, "☆");
}

export default function MechanicFinder() {
  const { user } = useApp();
  const navigate = useNavigate();
  const { citySlug } = useParams();
  const [cityQuery, setCityQuery] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [selectedService, setSelectedService] = useState("all");
  const [mechanics, setMechanics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [toast, setToast] = useState("");
  const [expandedReviews, setExpandedReviews] = useState([]);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [submitSubmitting, setSubmitSubmitting] = useState(false);
  const [submitForm, setSubmitForm] = useState({
    name: "",
    shop_name: "",
    city: "",
    village: "",
    district: "",
    state: "",
    phone_whatsapp: "",
    specialization: [],
    services: [],
    open_time: "09:00",
    close_time: "18:00",
    is_open_sunday: false,
    lat: "",
    lng: "",
    verification_method: "manual",
    verification_notes: "",
  });
  const [availableDistricts, setAvailableDistricts] = useState(
    submitForm.state ? getDistrictsForState(submitForm.state) : []
  );

  useEffect(() => {
    if (!citySlug) return;
    const resolved = citySlug
      .split("-")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
    setCityQuery(resolved);
  }, [citySlug]);

  useEffect(() => {
    if (!citySlug || !cityQuery) return;
    handleSearch(cityQuery, selectedBrand, selectedService);
  }, [citySlug, cityQuery, selectedBrand, selectedService]);

  const pageTitle = cityQuery
    ? `Bike Mechanics in ${cityQuery} | MotoHub India`
    : "Mechanic Finder | MotoHub India";
  const pageDescription = cityQuery
    ? `Find verified bike mechanics in ${cityQuery}. Compare specialties, ratings, and WhatsApp contact on MotoHub India.`
    : "Find verified motorcycle mechanics in your city, chat on WhatsApp, and read rider reviews on MotoHub India.";
  const pagePath = citySlug ? `/mechanic-finder/${citySlug}` : "/mechanic-finder";

  const handleStateChange = (newState) => {
    setSubmitForm((prev) => ({
      ...prev,
      state: newState,
      district: "",
    }));
    setAvailableDistricts(getDistrictsForState(newState));
  };

  useEffect(() => {
    if (!user?.city || hasSearched) return;
    handleSearch(user.city, selectedBrand, selectedService);
  }, [user?.city]);

  const locationLinks = [
    { label: "Pune", path: "/mechanic-finder/pune" },
    { label: "Mumbai", path: "/mechanic-finder/mumbai" },
    { label: "Bengaluru", path: "/mechanic-finder/bengaluru" },
    { label: "Chennai", path: "/mechanic-finder/chennai" },
    { label: "Delhi", path: "/mechanic-finder/delhi" },
  ];

  const activeFilters = useMemo(
    () => ({
      brand: selectedBrand,
      service: selectedService,
    }),
    [selectedBrand, selectedService]
  );

  async function handleSearch(location = cityQuery, brand = selectedBrand, service = selectedService) {
    const query = String(location || "").trim();
    setLoading(true);
    setHasSearched(true);

    const { data, error } = await getMechanicsByCity(query, { brand, service });
    if (error) {
      setToast(error.message || "Unable to load mechanics right now");
      setMechanics([]);
    } else {
      setMechanics(data || []);
      if (query && data?.length === 0) {
        setToast(`No mechanics found in ${query}`);
      }
    }
    setLoading(false);
  }

  const refreshMechanic = async (mechanicId) => {
    const { data, error } = await getMechanicWithReviews(mechanicId);
    if (error || !data) return;

    setMechanics((prev) =>
      prev.map((mechanic) => (mechanic.id === mechanicId ? { ...mechanic, ...data } : mechanic))
    );
  };

  const toggleReviews = (mechanicId) => {
    setExpandedReviews((prev) =>
      prev.includes(mechanicId) ? prev.filter((id) => id !== mechanicId) : [...prev, mechanicId]
    );
  };

  const openReviewModal = (mechanic) => {
    if (!mechanic) return;
    setReviewTarget(mechanic);
    setReviewRating(5);
    setReviewText("");
  };

  const handleSubmitReview = async (event) => {
    event.preventDefault();
    if (!reviewTarget) return;

    setReviewSubmitting(true);
    const { error } = await submitReview(reviewTarget.id, reviewRating, reviewText);
    if (error) {
      setToast(error.message || "Unable to save review");
    } else {
      setToast("Review submitted");
      await refreshMechanic(reviewTarget.id);
      setReviewTarget(null);
    }
    setReviewSubmitting(false);
  };

  const handleSubmitMechanic = async (event) => {
    event.preventDefault();
    
    console.log("🔍 Form validation starting...", submitForm);
    
    // Validate required fields
    if (!submitForm.name.trim()) {
      setToast("❌ Mechanic name is required");
      return;
    }
    if (!submitForm.shop_name.trim()) {
      setToast("❌ Shop name is required");
      return;
    }
    if (!submitForm.state) {
      setToast("❌ Please select a state");
      return;
    }
    if (!submitForm.district) {
      setToast("❌ Please select a district");
      return;
    }
    if (!submitForm.city.trim()) {
      setToast("❌ City name is required");
      return;
    }
    if (!submitForm.village.trim()) {
      setToast("❌ Village name is required");
      return;
    }
    if (!submitForm.phone_whatsapp || submitForm.phone_whatsapp.replace(/\D/g, "").length < 10) {
      setToast("❌ Valid WhatsApp number is required (10 digits)");
      return;
    }
    if (!submitForm.specialization.length) {
      setToast("❌ Select at least one specialization");
      return;
    }
    if (!submitForm.services.length) {
      setToast("❌ Select at least one service");
      return;
    }
    
    console.log("✅ All validations passed, submitting...");
    setSubmitSubmitting(true);
    
    console.log("📤 Submitting mechanic:", submitForm);

    const { data, error } = await submitMechanic({
      ...submitForm,
      lat: submitForm.lat ? Number(submitForm.lat) : null,
      lng: submitForm.lng ? Number(submitForm.lng) : null,
    });
    
    console.log("✅ Submission result - Error:", error, "Data:", data);

    if (error) {
      setToast(`❌ ${error.message || "Unable to submit mechanic"}`);
      console.error("❌ Submit error:", error);
    } else {
      setToast("✅ Mechanic submitted! Go to /mechanic-approval to approve it.");
      console.log("✅ Mechanic saved:", data);
      setSubmitForm({
        name: "",
        shop_name: "",
        city: "",
        village: "",
        district: "",
        state: "",
        phone_whatsapp: "",
        specialization: [],
        services: [],
        open_time: "09:00",
        close_time: "18:00",
        is_open_sunday: false,
        lat: "",
        lng: "",
        verification_method: "manual",
        verification_notes: "",
      });
      setAvailableDistricts([]);
      if (cityQuery.trim()) {
        await handleSearch(cityQuery, activeFilters.brand, activeFilters.service);
      }
    }

    setSubmitSubmitting(false);
  };

  const toggleSelection = (field, value) => {
    setSubmitForm((prev) => {
      const current = Array.isArray(prev[field]) ? prev[field] : [];
      const normalized = String(value).toLowerCase();
      if (field === "specialization" && normalized === "all") {
        return current.includes("all")
          ? { ...prev, [field]: [] }
          : { ...prev, [field]: ["all"] };
      }

      if (field === "specialization" && current.includes("all")) {
        return { ...prev, [field]: [normalized] };
      }

      return current.includes(normalized)
        ? { ...prev, [field]: current.filter((item) => item !== normalized) }
        : { ...prev, [field]: [...current.filter((item) => item !== "all"), normalized] };
    });
  };

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  return (
    <div className="mechanic-page">
      <PageSeo
        title={pageTitle}
        description={pageDescription}
        keywords={[
          "mechanic finder India",
          "bike mechanic near me",
          "motorcycle service India",
          "verified mechanic WhatsApp",
          cityQuery ? `${cityQuery} mechanic` : "bike mechanic",
        ]}
        path={pagePath}
        noindex={false}
      />

      <section className="mechanic-hero">
        <div className="mechanic-shell mechanic-hero__grid">
          <div>
            <p className="mechanic-kicker">Public rider help</p>
            <h1>Find trusted mechanics near your city or village</h1>
            <p className="mechanic-subtitle">
              Search verified shops, compare specialties, read rider reviews, and message mechanics on WhatsApp without leaving MotoHub India.
            </p>
            <div className="mechanic-stats">
              {HERO_STATS.map((item) => (
                <article key={item.label} className="mechanic-stat">
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </article>
              ))}
            </div>
          </div>

          <div className="mechanic-hero-card">
            <div className="mechanic-hero-card__title">Quick verify flow</div>
            <p>
              Submitted mechanics stay pending until admin verification. You can add WhatsApp, service scope, and optional map or OTP notes when submitting a shop.
            </p>
            <div className="mechanic-note">
              Best for: city repair, roadside help, village garages, and brand-specific specialists.
            </div>
            {user?.role === "mechanic" ? (
              <Link to="/my-garage" className="mechanic-secondary-btn" style={{ display: "inline-flex", width: "fit-content", marginTop: 14 }}>
                Open My Garage
              </Link>
            ) : (
              <button
                type="button"
                className="mechanic-secondary-btn"
                style={{ display: "inline-flex", width: "fit-content", marginTop: 14 }}
                onClick={() => navigate("/login")}
              >
                Login to submit
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="mechanic-shell mechanic-search-panel">
        <form
          className="mechanic-search-form"
          onSubmit={(event) => {
            event.preventDefault();
            handleSearch();
          }}
        >
          <label className="mechanic-field mechanic-field--wide">
            <span>Search city or village</span>
            <input
              type="text"
              value={cityQuery}
              onChange={(event) => setCityQuery(event.target.value)}
              placeholder="Pune, Lonavala, Coorg village..."
            />
          </label>

          <label className="mechanic-field">
            <span>Bike brand</span>
            <select value={selectedBrand} onChange={(event) => setSelectedBrand(event.target.value)}>
              {BRAND_OPTIONS.map((brand) => (
                <option key={brand} value={brand}>
                  {brand === "all" ? "All brands" : titleCase(brand)}
                </option>
              ))}
            </select>
          </label>

          <label className="mechanic-field">
            <span>Service type</span>
            <select value={selectedService} onChange={(event) => setSelectedService(event.target.value)}>
              {SERVICE_OPTIONS.map((service) => (
                <option key={service} value={service}>
                  {service === "all" ? "All services" : titleCase(service)}
                </option>
              ))}
            </select>
          </label>

          <button type="submit" className="mechanic-search-btn">
            Search Mechanics
          </button>
        </form>

        <div className="mechanic-chip-row">
          <button type="button" className={`mechanic-chip ${selectedBrand === "all" ? "is-active" : ""}`} onClick={() => setSelectedBrand("all")}>
            All Brands
          </button>
          {BRAND_OPTIONS.filter((brand) => brand !== "all").map((brand) => (
            <button
              key={brand}
              type="button"
              className={`mechanic-chip ${selectedBrand === brand ? "is-active" : ""}`}
              onClick={() => setSelectedBrand(brand)}
            >
              {titleCase(brand)}
            </button>
          ))}
          {SERVICE_OPTIONS.filter((service) => service !== "all").map((service) => (
            <button
              key={service}
              type="button"
              className={`mechanic-chip ${selectedService === service ? "is-active" : ""}`}
              onClick={() => setSelectedService(service)}
            >
              {titleCase(service)}
            </button>
          ))}
        </div>
      </section>

      <section className="mechanic-shell mechanic-search-panel" style={{ marginTop: 16 }}>
        <div className="mechanic-section-head">
          <div>
            <p className="mechanic-kicker">Popular mechanic cities</p>
            <h2>Browse mechanic pages by city</h2>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {locationLinks.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              style={{
                padding: "8px 12px",
                borderRadius: 999,
                border: "1px solid var(--border)",
                background: "var(--card)",
                color: "var(--text)",
                textDecoration: "none",
                fontSize: 13,
              }}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </section>

      <section className="mechanic-shell mechanic-results">
        <div className="mechanic-results__head">
          <div>
            <h2>Mechanics near you</h2>
            <p>
              {hasSearched
                ? `Showing verified mechanics for ${cityQuery.trim() || "your location"}`
                : "Search by city or village to begin"}
            </p>
          </div>
          <div className="mechanic-results__count">{mechanics.length} results</div>
        </div>

        {loading ? (
          <div className="mechanic-state">Loading mechanics...</div>
        ) : mechanics.length === 0 && hasSearched ? (
          <div className="mechanic-state mechanic-state--empty">
            <h3>No mechanics found in {cityQuery.trim() || "this area"}. Be first to add one!</h3>
            <p>Use the submit form below to add a trusted garage and help other riders nearby.</p>
            <button
              type="button"
              className="mechanic-secondary-btn"
              onClick={() => document.getElementById("mechanic-submit-form")?.scrollIntoView({ behavior: "smooth", block: "start" })}
            >
              Add a Mechanic
            </button>
          </div>
        ) : (
          <div className="mechanic-grid">
            {mechanics.map((mechanic) => {
              const reviewsOpen = expandedReviews.includes(mechanic.id);
              const waLink = toWhatsAppLink(mechanic.phone_whatsapp);
              return (
                <article key={mechanic.id} className="mechanic-card">
                  <div className="mechanic-card__top">
                    <div>
                      <p className="mechanic-shop">{mechanic.shop_name}</p>
                      <h3>{mechanic.name}</h3>
                    </div>
                    <div className="mechanic-top-badges">
                      {mechanic.is_verified ? <span className="mechanic-badge mechanic-badge--verified">Verified</span> : null}
                      <span className="mechanic-badge mechanic-badge--city">{mechanic.city}</span>
                    </div>
                  </div>

                  <div className="mechanic-location-row">
                    <span>{mechanic.village}</span>
                    <span>{mechanic.district}</span>
                    <span>{mechanic.state}</span>
                  </div>

                  <div className="mechanic-rating-row">
                    <div className="mechanic-stars" aria-label={`Rating ${mechanic.avg_rating || 0} out of 5`}>
                      <span>{starString(mechanic.avg_rating || 0)}</span>
                      <strong>{Number(mechanic.avg_rating || 0).toFixed(1)}</strong>
                    </div>
                    <span className="mechanic-review-count">{mechanic.review_count || 0} reviews</span>
                  </div>

                  <div className="mechanic-open-hours">
                    <span>
                      {formatTime(mechanic.open_time)} - {formatTime(mechanic.close_time)}
                    </span>
                    <span>{mechanic.is_open_sunday ? "Open Sunday" : "Closed Sunday"}</span>
                  </div>

                  <div className="mechanic-tags">
                    {(mechanic.specialization || []).map((tag) => (
                      <span key={`${mechanic.id}-spec-${tag}`} className="mechanic-tag mechanic-tag--brand">
                        {titleCase(tag)}
                      </span>
                    ))}
                  </div>

                  <div className="mechanic-tags">
                    {(mechanic.services || []).map((tag) => (
                      <span key={`${mechanic.id}-srv-${tag}`} className="mechanic-tag">
                        {titleCase(tag)}
                      </span>
                    ))}
                  </div>

                  <div className="mechanic-actions">
                    <a
                      href={waLink || "#"}
                      target="_blank"
                      rel="noreferrer"
                      className={`mechanic-primary-btn ${waLink ? "" : "is-disabled"}`}
                      onClick={(event) => {
                        if (!waLink) event.preventDefault();
                      }}
                    >
                      WhatsApp
                    </a>
                    <button type="button" className="mechanic-secondary-btn" onClick={() => toggleReviews(mechanic.id)}>
                      {reviewsOpen ? "Hide Reviews" : "View Reviews"}
                    </button>
                    <button
                      type="button"
                      className="mechanic-secondary-btn"
                      onClick={() => {
                        if (!user?.id) {
                          navigate("/login");
                          return;
                        }
                        openReviewModal(mechanic);
                      }}
                    >
                      Write Review
                    </button>
                  </div>

                  {reviewsOpen ? (
                    <div className="mechanic-review-list">
                      {(mechanic.reviews || []).length ? (
                        mechanic.reviews.slice(0, 3).map((review) => (
                          <article key={review.id} className="mechanic-review-item">
                            <div className="mechanic-review-item__top">
                              <div className="mechanic-review-avatar">R</div>
                              <div>
                                <strong>{starString(review.rating)}</strong>
                                <span>{new Date(review.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                              </div>
                            </div>
                            <p>{review.review_text}</p>
                          </article>
                        ))
                      ) : (
                        <p className="mechanic-empty-reviews">No reviews yet. Be the first rider to review this garage.</p>
                      )}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className="mechanic-shell mechanic-submit" id="mechanic-submit-form">
        <div className="mechanic-section-head">
          <div>
            <p className="mechanic-kicker">Add a trusted garage</p>
            <h2>Submit a mechanic</h2>
          </div>
          <p className="mechanic-submit-note">Submitted mechanics go live after admin verification.</p>
        </div>

        <form className="mechanic-submit-form" onSubmit={handleSubmitMechanic}>
          <label className="mechanic-field">
            <span>Mechanic name</span>
            <input required value={submitForm.name} onChange={(event) => setSubmitForm((prev) => ({ ...prev, name: event.target.value }))} />
          </label>

          <label className="mechanic-field">
            <span>Shop name</span>
            <input required value={submitForm.shop_name} onChange={(event) => setSubmitForm((prev) => ({ ...prev, shop_name: event.target.value }))} />
          </label>

          <label className="mechanic-field">
            <span>State</span>
            <select required value={submitForm.state} onChange={(event) => handleStateChange(event.target.value)}>
              <option value="">Select State</option>
              {INDIAN_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </label>

          <label className="mechanic-field">
            <span>District</span>
            <select required value={submitForm.district} onChange={(event) => setSubmitForm((prev) => ({ ...prev, district: event.target.value }))} disabled={!submitForm.state}>
              <option value="">Select District</option>
              {availableDistricts.map((district) => (
                <option key={district} value={district}>
                  {district}
                </option>
              ))}
            </select>
          </label>

          <label className="mechanic-field">
            <span>City</span>
            <input required value={submitForm.city} onChange={(event) => setSubmitForm((prev) => ({ ...prev, city: event.target.value }))} />
          </label>

          <label className="mechanic-field">
            <span>Village</span>
            <input required value={submitForm.village} onChange={(event) => setSubmitForm((prev) => ({ ...prev, village: event.target.value }))} />
          </label>

          <label className="mechanic-field">
            <span>WhatsApp number</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 600, color: "var(--text2)" }}>+91</span>
              <input
                required
                type="tel"
                value={(submitForm.phone_whatsapp || "").replace(/^\+?91\s*/, "")}
                onChange={(event) => {
                  const cleaned = event.target.value.replace(/\D/g, "").slice(0, 10);
                  setSubmitForm((prev) => ({ ...prev, phone_whatsapp: cleaned ? `+91${cleaned}` : "" }));
                }}
                placeholder="98765 43210"
                maxLength="10"
                style={{ flex: 1 }}
              />
            </div>
          </label>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label className="mechanic-field">
              <span>Open time (24hr)</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="time"
                  value={submitForm.open_time}
                  onChange={(event) => setSubmitForm((prev) => ({ ...prev, open_time: event.target.value }))}
                  style={{ flex: 1 }}
                />
                <span style={{ fontSize: 12, color: "var(--text2)", minWidth: 50 }}>{convertTo24HourFormat(submitForm.open_time)}</span>
              </div>
            </label>

            <label className="mechanic-field">
              <span>Close time (24hr)</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="time"
                  value={submitForm.close_time}
                  onChange={(event) => setSubmitForm((prev) => ({ ...prev, close_time: event.target.value }))}
                  style={{ flex: 1 }}
                />
                <span style={{ fontSize: 12, color: "var(--text2)", minWidth: 50 }}>{convertTo24HourFormat(submitForm.close_time)}</span>
              </div>
            </label>
          </div>

          <label className="mechanic-field mechanic-field--checkbox">
            <span>Open on Sunday</span>
            <input
              type="checkbox"
              checked={submitForm.is_open_sunday}
              onChange={(event) => setSubmitForm((prev) => ({ ...prev, is_open_sunday: event.target.checked }))}
            />
          </label>

          <label className="mechanic-field">
            <span>Google map / location share note</span>
            <input
              value={submitForm.verification_notes}
              onChange={(event) => setSubmitForm((prev) => ({ ...prev, verification_notes: event.target.value }))}
              placeholder="Optional map link or OTP reference"
            />
          </label>

          <label className="mechanic-field">
            <span>Verification source</span>
            <select value={submitForm.verification_method} onChange={(event) => setSubmitForm((prev) => ({ ...prev, verification_method: event.target.value }))}>
              <option value="manual">Manual review</option>
              <option value="mobile_otp">Mobile OTP</option>
              <option value="map_location">Map location share</option>
            </select>
          </label>

          <div className="mechanic-multi-select">
            <span>Specialization</span>
            <div className="mechanic-chip-row">
              {BRAND_OPTIONS.map((brand) => (
                <button
                  key={`submit-brand-${brand}`}
                  type="button"
                  className={`mechanic-chip ${submitForm.specialization.includes(brand) ? "is-active" : ""}`}
                  onClick={() => toggleSelection("specialization", brand)}
                >
                  {brand === "all" ? "All Bikes" : titleCase(brand)}
                </button>
              ))}
            </div>
          </div>

          <div className="mechanic-multi-select">
            <span>Services</span>
            <div className="mechanic-chip-row">
              {MECHANIC_SERVICES.map((service) => (
                <button
                  key={`submit-service-${service}`}
                  type="button"
                  className={`mechanic-chip ${submitForm.services.includes(service) ? "is-active" : ""}`}
                  onClick={() => toggleSelection("services", service)}
                >
                  {titleCase(service)}
                </button>
              ))}
            </div>
          </div>

          <button type="submit" className="mechanic-submit-btn" disabled={submitSubmitting}>
            {submitSubmitting ? "Submitting..." : "Submit Mechanic"}
          </button>
        </form>
      </section>

      {reviewTarget ? (
        <div className="mechanic-modal" role="presentation" onClick={() => setReviewTarget(null)}>
          <div className="mechanic-modal__card" onClick={(event) => event.stopPropagation()}>
            <div className="mechanic-modal__head">
              <div>
                <p className="mechanic-kicker">Write review</p>
                <h3>{reviewTarget.shop_name}</h3>
              </div>
              <button type="button" className="mechanic-modal__close" onClick={() => setReviewTarget(null)}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmitReview}>
              <div className="mechanic-rating-picker">
                {[5, 4, 3, 2, 1].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    className={`mechanic-rating-btn ${reviewRating === rating ? "is-active" : ""}`}
                    onClick={() => setReviewRating(rating)}
                  >
                    {rating}★
                  </button>
                ))}
              </div>

              <label className="mechanic-field">
                <span>Your review</span>
                <textarea
                  rows="5"
                  value={reviewText}
                  onChange={(event) => setReviewText(event.target.value)}
                  placeholder="Tell other riders about service quality, pricing, and turnaround time..."
                />
              </label>

              <div className="mechanic-modal__actions">
                <button type="button" className="mechanic-secondary-btn" onClick={() => setReviewTarget(null)}>
                  Cancel
                </button>
                <button type="submit" className="mechanic-primary-btn" disabled={reviewSubmitting}>
                  {reviewSubmitting ? "Saving..." : "Submit Review"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {toast ? <div className="mechanic-toast">{toast}</div> : null}
    </div>
  );
}
